import React, { useEffect, useMemo } from 'react';
import delve from 'dlv';
import PlaylistPerformance from 'components/data-containers/Overview/PlaylistPerformance/PlaylistPerformance';
import DownloadPerformance from 'components/data-containers/Overview/DownloadPerformance/DownloadPerformance';
import RadioPerformance from 'components/data-containers/Overview/RadioPerformance/RadioPerformance';
import TerritoryPerformance from 'components/data-containers/Overview/TerritoryPerformance/TerritoryPerformance';
import StreamingPerformance from 'components/data-containers/Overview/StreamingPerformance/StreamingPerformance';
import SocialPerformance from 'components/data-containers/Overview/SocialPerformance/SocialPerformance';
import PhysicalPerformance from 'components/data-containers/Overview/PhysicalPerformance/PhysicalPerformance';
import DSPChartPerformance from 'components/data-containers/Overview/DSPChartPerformance/DSPChartPerformance';
import OfficialChartPerformance from 'components/data-containers/Overview/OfficialChartPerformance/OfficialChartPerformance';
import ShortFormVideoPerformance from 'components/data-containers/Overview/ShortFormVideoPerformance/ShortFormVideoPerformance';
import ShazamOverview from 'components/data-containers/Overview/ShazamOverview/ShazamOverview';

import Modal from 'components/utility/Modal/Modal';
import CustomizeView, {
  TItemCustomViewExtended,
} from 'components/CustomizeView/CustomizeView';

import useArtistIds from 'hooks/useArtistIds';
import { EntityContext } from 'context/entityContext';
import { useDispatch, useSelector } from 'react-redux';

import {
  IUserSettingsState,
  loadOrInitUserSettings,
  shouldLoadUserSettings,
} from 'redux/reducers/userSettings';
import { RootState } from 'redux/rootReducer';
import { TAsyncDispatchResult } from 'types/shared';
import { setCustomizeView } from 'redux/reducers/user/customizeView';
import { ISectionComponent, getSections } from './shared';

interface IOverviewSectionsProps {
  setShowModal: (value: boolean) => void;
  isModalShown: boolean;
}

const OverviewSections: React.FC<IOverviewSectionsProps> = (props) => {
  const { setShowModal, isModalShown } = props;
  const ctx = React.useContext(EntityContext);
  const { entityType } = ctx;
  const dispatch = useDispatch();
  const artistIds = useArtistIds();
  const viewName = `overview_summary_${entityType}`;
  const sections = getSections(entityType);

  const userSettings = useSelector(
    (state: RootState): IUserSettingsState => state.userSettings
  );

  const userSettingsLoaded = useMemo(
    () => delve(userSettings, ['success', 'all'], false),
    [userSettings]
  );

  const userSections = useMemo(() => {
    if (userSettingsLoaded) {
      const customizeView = userSettings.data.customizeViews[viewName];

      return customizeView !== null ? customizeView : sections; // user don't have any settings for Overview page
    }
    return [];
  }, [sections, viewName, userSettings, userSettingsLoaded]);

  const saveSections = (
    sections: TItemCustomViewExtended[]
  ): TAsyncDispatchResult => dispatch(setCustomizeView(viewName, sections));

  useEffect(() => {
    if (dispatch(shouldLoadUserSettings())) {
      dispatch(loadOrInitUserSettings());
    }
  }, [dispatch]);

  const showPlaylistData = entityType === 'track' || entityType === 'release';
  const productType = entityType === 'release' ? undefined : 'track';

  const sectionComponents: ISectionComponent = {
    streamingPerformance: <StreamingPerformance />,
    playlists: <PlaylistPerformance showPlaylistData={showPlaylistData} />,
    dspCharts: <DSPChartPerformance {...ctx} productType={productType} />,
    officialCharts: (
      <OfficialChartPerformance {...ctx} productType={productType} />
    ),
    radioPerformance: <RadioPerformance />,
    shazam: <ShazamOverview />,
    downloadPerformance: <DownloadPerformance />,
    physicalPerformance: <PhysicalPerformance />,
    territory: <TerritoryPerformance />,
    shortFormVideo: <ShortFormVideoPerformance />,
    socialPerformance: <SocialPerformance artistIds={artistIds} {...ctx} />,
  };

  return (
    <>
      {userSections.map((section: TItemCustomViewExtended) => (
        <section key={section.value}>
          {sectionComponents[section.value]}
        </section>
      ))}

      {isModalShown && (
        <Modal
          hide={(): void => setShowModal(false)}
          canClickOutside={true}
          isNarrow={true}
        >
          <CustomizeView
            title="Overview Blocks"
            hideModal={(): void => setShowModal(false)}
            saveToApi={saveSections}
            savedItems={userSections}
            allAvailableItems={sections}
            displayBlockLabel="Display Blocks"
            orderBlockLabel="Order Blocks"
            isLow
            canTurnOffAll
          />
        </Modal>
      )}
    </>
  );
};

export default OverviewSections;
